/**
 * Local backup of in-flight edits.
 *
 * Until a save succeeds, a document's content exists only in React state. A
 * failed save, a closed tab, or a browser crash took it with them — which is
 * exactly what happened during the expired-session bug, when every save 401'd
 * for hours with no backup anywhere.
 *
 * IndexedDB rather than localStorage on purpose: localStorage is synchronous
 * and would block the main thread on every debounce tick, which is precisely
 * the kind of hitch that makes autosave *felt*. Writes here are async, fired
 * off the critical path, and never awaited by the editor.
 */

const DB_NAME = "sermonspace";
const DB_VERSION = 1;
const STORE = "drafts";

export interface LocalDraft {
  documentId: number;
  content: string;
  /** Title and body are one revision; keeping them together prevents a fast
   * navigation from preserving one but losing the other. */
  title?: string;
  /** Monotonic editor revision used to decide what has reached the server. */
  revision?: number;
  /** Server version this draft was based on, for conflict reasoning. */
  baseVersion: number | null;
  savedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    // Private browsing and some embedded webviews reject IndexedDB entirely.
    // A missing backup must never break the editor, so failure resolves null
    // and every operation below becomes a no-op.
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "documentId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Record the current buffer. Fire-and-forget — never await this while typing. */
export function saveLocalDraft(draft: LocalDraft): void {
  void withStore("readwrite", (store) => store.put(draft));
}

export async function readLocalDraft(documentId: number): Promise<LocalDraft | null> {
  return withStore<LocalDraft>("readonly", (store) => store.get(documentId));
}

/** Called once a save succeeds — the server now has it, so the backup is noise. */
export function clearLocalDraft(documentId: number): void {
  void withStore("readwrite", (store) => store.delete(documentId));
}
